import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Loader2, Lock, Shield } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { sanitizeSecureFormFields } from '../components/chat/SecureFormBubble';
import {
  SecureFormCard,
  SecureFormFieldControl,
  SecureFormFieldLabel,
  formatSsnInput,
} from '../components/chat/secureFormUi';
import {
  fetchPublicSecureForm,
  submitPublicSecureForm,
  type PublicSecureForm,
  type SecureFormField,
} from '../lib/api';

export default function SecureFormPublic() {
  const { token = '' } = useParams<{ token: string }>();
  const [form, setForm] = useState<PublicSecureForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setError('Invalid link');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await fetchPublicSecureForm(token);
    if (!res.ok || !res.data) {
      const msg = res.error?.message ?? '';
      const status = res.error?.status ?? res.status;
      if (status === 410 || /expired/i.test(msg)) {
        setError('This secure form link has expired.');
      } else {
        setError(msg || 'This secure form link is invalid or expired.');
      }
      setForm(null);
      setLoading(false);
      return;
    }
    setForm(res.data);
    setDone(Boolean(res.data.submitted || res.data.all_required_filled));
    setLoading(false);
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const fields: SecureFormField[] = useMemo(
    () => sanitizeSecureFormFields(form?.fields),
    [form?.fields]
  );

  const canSave = useMemo(() => {
    if (fields.length === 0) return false;
    const required = fields.filter((f) => f.required !== false);
    return required.every((f) => (values[f.key] ?? '').trim().length > 0);
  }, [fields, values]);

  const handleChange = (key: string, raw: string, type: SecureFormField['type']) => {
    const next = type === 'ssn' ? formatSsnInput(raw) : raw;
    setValues((prev) => ({ ...prev, [key]: next }));
  };

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, string> = {};
      for (const f of fields) {
        const v = (values[f.key] ?? '').trim();
        if (v) payload[f.key] = v;
      }
      if (Object.keys(payload).length === 0) {
        setError('Enter at least one field before submitting.');
        return;
      }
      const res = await submitPublicSecureForm(token, payload);
      if (!res.ok || !res.data) {
        const msg = res.error?.message ?? 'Submit failed';
        const status = res.error?.status ?? res.status;
        if (status === 410 || /expired/i.test(msg)) {
          setError('This secure form link has expired.');
        } else if (status === 404 || /not found/i.test(msg)) {
          setError('This secure form link is invalid.');
        } else if (status === 409 || /already been submitted/i.test(msg)) {
          setError('This form has already been submitted.');
          await load();
        } else if (status === 502 || status === 503 || status === 504 || status === 0) {
          setError(
            /still unavailable/i.test(msg)
              ? msg
              : 'Server temporarily unavailable after multiple attempts. Please try again in a moment.'
          );
        } else {
          setError(msg);
        }
        return;
      }
      const filled = Array.isArray(res.data.filled_keys) ? res.data.filled_keys.length : 0;
      if (res.data.submitted || res.data.all_required_filled || filled > 0) {
        setDone(true);
      } else {
        setError('Submit did not store any fields. Please try again.');
      }
      await load();
    } finally {
      setSaving(false);
    }
  };

  const title = (form?.title || 'Secure information').replace(/[*_`#[\]|]/g, '').trim().slice(0, 120);

  return (
    <div className="flex min-h-screen items-start justify-center bg-zt-bg px-4 py-10 text-slate-200 sm:py-14">
      <div className="w-full max-w-lg">
        <div className="mb-7 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg border border-zt-primary/30 bg-zt-surface">
            <Shield className="text-zt-primary" size={18} />
          </div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-zt-primary">ZeroTail</p>
          <h1 className="mt-2 text-xl font-semibold tracking-tight text-slate-50">Secure information form</h1>
          <p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-zt-muted">
            Encrypted submission — values are never shown to the AI assistant.
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-zt-muted">
            <Loader2 size={16} className="animate-spin" /> Loading form…
          </div>
        )}

        {!loading && error && !form && (
          <div className="rounded-xl border border-zt-danger/40 bg-zt-danger/10 px-4 py-6 text-center text-sm text-zt-danger">
            {error}
          </div>
        )}

        {!loading && form && (
          <SecureFormCard title={title} badge="Encrypted">
            {form.recipient_name && (
              <p className="text-xs text-zt-muted">
                For:{' '}
                <span className="font-medium text-emerald-100">
                  {String(form.recipient_name).slice(0, 120)}
                </span>
              </p>
            )}

            {done ? (
              <div className="flex items-start gap-2.5 rounded-lg border border-zt-primary/30 bg-zt-primary/10 px-3.5 py-3.5 text-sm text-emerald-50">
                <Check size={16} className="mt-0.5 shrink-0 text-zt-primary" />
                <div>
                  <p className="font-medium">Submitted securely</p>
                  <p className="mt-1 text-xs text-zt-muted">Thank you. You can close this page.</p>
                </div>
              </div>
            ) : (
              <>
                <p className="text-xs leading-relaxed text-zt-muted">
                  Enter only your own information on this form. Values are encrypted at rest.
                </p>

                {fields.length === 0 && (
                  <p className="text-xs text-zt-warning">
                    This form has no valid fields. Contact the sender for a new link.
                  </p>
                )}

                {fields.map((field) => (
                  <label key={field.key} className="block min-w-0">
                    <SecureFormFieldLabel label={field.label} required={field.required !== false} />
                    <SecureFormFieldControl
                      field={field}
                      value={values[field.key] ?? ''}
                      onChange={(raw) => handleChange(field.key, raw, field.type)}
                    />
                  </label>
                ))}

                {error && <p className="break-words text-xs text-zt-danger">{error}</p>}

                <button
                  type="button"
                  disabled={!canSave || saving}
                  onClick={() => void handleSave()}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-zt-primary px-3 py-2.5 text-sm font-medium text-zt-bg hover:bg-zt-accent disabled:opacity-40"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                  Submit securely
                </button>
              </>
            )}

            {form.expires_at && !done && (
              <p className="text-[11px] text-zt-muted/70">
                Link expires {new Date(form.expires_at).toLocaleString()}
              </p>
            )}
          </SecureFormCard>
        )}
      </div>
    </div>
  );
}
